import { useMemo } from "react";
import { Badge, Button, Card, Image, Typography, Flex } from "antd";
import Slider from "react-slick";
import { useNavigate } from "react-router-dom";

import {
  NextArrow,
  PrevArrow,
} from "../../Components/Common/reactSlickCustomArrows";

const { Text } = Typography;

export default function BestSellerCarousel({
  category,
  screens,
  settings,
  toSlug,
}) {
  const navigate = useNavigate();

  const products = category?.Products ?? [];

  const isMobile = screens?.xs;
  const isStacked = !screens?.xl;

  const { slidesToShow, canSlide } = useMemo(() => {
    const desired = isMobile ? 2.35 : screens?.md ? 2 : 1;
    const total = products.length;
    const slides = Math.min(desired, total);

    return { slidesToShow: slides, canSlide: total > slides };
  }, [isMobile, screens?.md, products.length]);

  if (!category || products.length === 0) return null;

  const sliderSettings = {
    variableWidth: !isMobile,
    slidesToShow,
    slidesToScroll: 1,
    infinite: !isMobile && canSlide,
    autoplay: !isMobile && canSlide,
    arrows: canSlide,
    dots: false,
    swipeToSlide: true,
    draggable: true,
    accessibility: false, // 🔥 stops aria-hidden click madness
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
  };

  return (
    <Flex
      style={{
        marginTop: 40,
        marginBottom: 40,
        marginLeft: "auto",
        marginRight: "auto",
        width: "100%",
        flexDirection: isStacked ? "column" : "row",
        gap: 0,
        maxWidth: 2500,
      }}
    >
      {/* LEFT – Yellow (overlapping) */}
      <div
        style={{
          backgroundColor: "#FFDC00",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: isMobile ? "center" : "flex-start",
          padding: 24,
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 10px 30px rgba(0,0,0,0.15)",

          // Reserve the overlapping panel for desktop widths.
          flex: isStacked ? "0 0 auto" : "0 0 420px",

          marginRight: isStacked ? 0 : -160,

          minHeight: isStacked ? 70 : 440,
          height: isStacked ? "auto" : "100%",

          width: isStacked ? "100%" : "auto",
          marginBottom: isStacked ? -20 : 0,
        }}
      >
        <p
          className="category-title"
          style={{
            fontSize: isStacked ? 22 : 30,
            margin: 0,
            textAlign: screens?.xs ? "center" : "left",
            width: isStacked ? "100%" : 200,
          }}
        >
          {category.CategoryName}
        </p>

        <Text
          className="view-more-title"
          style={{
            fontSize: screens.xs ? 10 : 14,
          }}
          onClick={() =>
            navigate("/products", {
              state: {
                category: {
                  Id: category.Id,
                },
              },
            })
          }
        >
          View more &gt;
        </Text>
      </div>

      {/* RIGHT – Content */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          width: "100%",
          marginTop: isStacked ? 20 : 10,

          // ✅ on desktop, push carousel right so overlap doesn't cover it
          // paddingLeft: isMobile ? 0 : 140,
        }}
      >
        <Slider
          {...sliderSettings}
          className="home-product-carousel"
          style={
            {
              //  marginTop: 30,
            }
          }
        >
          {products.map((item) => {
            const categoryText = (item.SubCategoryNames ?? []).join(", ");

            const card = (
              <Card
                styles={{
                  header: {
                    height: isMobile ? 54 : 72,
                    minHeight: isMobile ? 54 : 72,
                    paddingLeft: isMobile ? 8 : 24,
                    paddingRight: isMobile ? 8 : 24,
                  },
                  title: {
                    height: "100%",
                    paddingTop: isMobile ? 8 : 12,
                    paddingBottom: isMobile ? 8 : 12,
                    overflow: "hidden",
                    whiteSpace: "normal",
                    boxSizing: "border-box",
                  },
                  cover: {
                    paddingTop: isMobile ? 6 : 8,
                  },
                  body: {
                    paddingTop: isMobile ? 8 : 10,
                    paddingBottom: 0,
                    paddingLeft: isMobile ? 8 : 24,
                    paddingRight: isMobile ? 8 : 24,
                    width: "100%",
                    minWidth: isMobile ? 0 : 225,
                    boxSizing: "border-box",
                  },
                  actions: {
                    paddingLeft: isMobile ? 8 : 20,
                    paddingRight: isMobile ? 8 : 20,
                    paddingTop: 0,
                    paddingBottom: 0,
                    margin: 0,
                    borderTop: "none",
                  },
                }}
                style={{
                  cursor: "initial",
                  width: isMobile ? "100%" : 280,
                  marginTop: isMobile ? 8 : 10,
                  overflow: "hidden",
                }}
                hoverable={false}
                title={
                  <Typography.Paragraph
                    ellipsis={{ rows: 2, tooltip: categoryText }}
                    style={{
                      margin: 0,
                      fontSize: isMobile ? 10 : 14,
                      lineHeight: isMobile ? 1.3 : 1.4,
                      color: "#6B7280",
                    }}
                  >
                    {categoryText}
                  </Typography.Paragraph>
                }
                cover={
                  <Image
                    width={isMobile || screens.md ? "100%" : 240}
                    height={isMobile ? 105 : 200}
                    alt={item.ProductName}
                    style={{
                      borderRadius: 4,
                      overflow: "hidden",
                      objectFit: "contain",
                    }}
                    src={(settings?.productImageFolder ?? "") + item.CoverImage}
                  />
                }
                actions={[
                  <Button
                    key="view"
                    type="primary"
                    block
                    style={{
                      marginTop: 0,
                      backgroundColor: "#002753",
                      color: "#fff",
                      height: isMobile ? 30 : undefined,
                      paddingLeft: isMobile ? 4 : undefined,
                      paddingRight: isMobile ? 4 : undefined,
                      fontSize: isMobile ? 11 : undefined,
                    }}
                    className="view-details-btn"
                    onClick={() => {
                      navigate(
                        `/product/${toSlug(item.ProductName)}/${toSlug(
                          item.Sku,
                        )}/${item.ProductId}`,
                      );
                    }}
                  >
                    View Details
                  </Button>,
                ]}
              >
                <Typography.Paragraph
                  ellipsis={{
                    rows: isMobile ? 3 : 2,
                    tooltip: item.ProductName,
                  }}
                  style={{
                    width: "100%",
                    minHeight: isMobile ? 48.6 : 50.5,
                    margin: 0,
                    fontSize: isMobile ? 12 : 16,
                    lineHeight: isMobile ? 1.35 : undefined,
                    color: "#002753",
                    fontWeight: "bold",
                  }}
                >
                  {item.ProductName}
                </Typography.Paragraph>

                <p
                  style={{
                    margin: 0,
                    marginTop: isMobile ? 4 : 5,
                    fontSize: isMobile ? 10 : 14,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: "#6B7280",
                  }}
                >
                  {item.Sku}
                </p>
              </Card>
            );

            let wrapped = card;

            if (item.Promotions && item.Promotions.length > 0) {
              wrapped = (
                <div style={{ position: "relative" }}>
                  <div
                    style={{
                      position: "absolute",
                      top: 10,
                      left: -12,
                      background: "#FFDC00",
                      padding: "2px 10px",
                      transform: "rotate(-45deg)",
                      fontWeight: "bold",
                      zIndex: 1,
                      boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                      borderRadius: 4,
                      overflow: "hidden",
                      color: "black",
                    }}
                  >
                    Promo
                  </div>
                  {wrapped}
                </div>
              );
            }

            if (item.RibbonText) {
              wrapped = (
                <Badge.Ribbon text={item.RibbonText} color="red">
                  {wrapped}
                </Badge.Ribbon>
              );
            }

            return (
              <div key={item.ProductId} className="find-me">
                {wrapped}
              </div>
            );
          })}
        </Slider>
      </div>
    </Flex>
  );
}
